/**
 * BarueriFlash - Push Notifications Hook
 * React hook for managing push notifications
 */
import { useState, useEffect, useCallback } from 'react';
import pushNotificationService, { formatNotificationData } from '../services/pushNotifications';
import { useAuth } from '../contexts/AuthContext';

export const useNotifications = () => {
  const { token, user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      try {
        const supported = await pushNotificationService.init();
        setIsSupported(supported);

        if (supported) {
          const status = pushNotificationService.getPermissionStatus();
          setPermission(status);

          const subscription = await pushNotificationService.getSubscription();
          setIsSubscribed(!!subscription);
        }
      } catch (err) {
        console.error('Notification init error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // Request permission and subscribe
  const enableNotifications = useCallback(async () => {
    if (!isSupported) {
      setError('Notificações não suportadas neste navegador');
      return false;
    }

    if (!token) {
      setError('Você precisa estar logado para ativar notificações');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await pushNotificationService.subscribe(token);
      setPermission('granted');
      setIsSubscribed(true);
      
      // Show welcome notification
      await pushNotificationService.showLocalNotification(
        '🔔 Notificações Ativadas!',
        {
          body: 'Você receberá atualizações sobre seus pedidos e entregas.',
          tag: 'welcome-notification'
        }
      );
      
      return true;
    } catch (err) {
      console.error('Failed to enable notifications:', err);
      setError(err.message);
      setPermission(pushNotificationService.getPermissionStatus());
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, token]);

  // Disable notifications
  const disableNotifications = useCallback(async () => {
    if (!token) return false;

    setLoading(true);
    try {
      await pushNotificationService.unsubscribe(token);
      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.error('Failed to disable notifications:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Show local notification
  const showNotification = useCallback(async (type, data = {}) => {
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    const notificationData = formatNotificationData(type, data);
    await pushNotificationService.showLocalNotification(
      notificationData.title,
      {
        body: notificationData.body,
        data: notificationData.data,
        actions: notificationData.actions,
        requireInteraction: notificationData.requireInteraction
      }
    );
  }, [permission]);

  return {
    isSupported,
    permission,
    isSubscribed,
    loading,
    error,
    enableNotifications,
    disableNotifications,
    showNotification
  };
};

export default useNotifications;
